import React, { useEffect, useState, useCallback, useRef } from 'react';
import { MessageSquare, Heart, Eye, Loader2 } from 'lucide-react';
import CharacterManager from './CharacterManager.tsx';
import Sidebar from './Sidebar.tsx';
import TopBar from './TopBar.tsx';
import ProfileModal from './ProfileModal.tsx';
import MobileNavigation from './MobileNavigation.tsx';
import LoginModal from './LoginModal.tsx';
import { useTheme } from '../context/ThemeContext.tsx';
import { useIsMobile } from '../hooks/useIsMobile.ts';
import { API_URL } from '../config/api.ts';

interface Character {
  _id: string;
  name: string;
  role?: string;
  description: string;
  personality: string;
  systemPrompt: string;
  avatar: string;
  backgroundImage: string;
  tags: string[];
  isActive?: boolean;
  isDefault: boolean;
  createdBy: string;
  isNSFW?: boolean;
  stats?: {
    views: number;
    likes: number;
    messagesCount: number;
    favorites: number;
  };
}



interface CharacterLobbyProps {
  onSelectCharacter: (character: Character) => void;
  onViewCharacterDetail: (character: Character) => void;
  onLogin?: () => void;
  onLogout?: () => void;
  onOpenPricing?: () => void;
}

const CharacterLobby: React.FC<CharacterLobbyProps> = ({
  onSelectCharacter,
  onViewCharacterDetail,
  onLogin,
  onLogout,
  onOpenPricing
}) => {
  const isMobile = useIsMobile();
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [characters, setCharacters] = useState<Character[]>([]);
  const [recentCharacters, setRecentCharacters] = useState<Character[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCharacterManager, setShowCharacterManager] = useState(false);
  const [startInCreateMode, setStartInCreateMode] = useState(false);
  const [activeTab, setActiveTab] = useState('Characters');
  const [activeFilter, setActiveFilter] = useState('Popular');
  const [searchText, setSearchText] = useState('');
  const [showNSFW, setShowNSFW] = useState(() => {
    const saved = localStorage.getItem('showNSFW');
    return saved === 'true';
  });
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(true);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [userStats, setUserStats] = useState<any>(null);

  // Liked IDs for efficient checking
  const [likedIds, setLikedIds] = useState<Set<string>>(new Set());

  // Pagination state
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const ITEMS_PER_PAGE = 24;

  // Login modal for non-authenticated like attempts
  const [showLoginModal, setShowLoginModal] = useState(false);

  const fetchUserProfile = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/user`, {
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success && data.user) {
        console.log('👤 CharacterLobby - FULL user data from API:', data.user);
        console.log('🔑 CharacterLobby - isOAuthUser:', data.user.isOAuthUser, 'hasPassword:', data.user.hasPassword);
        setUserStats(data.user);
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
    }
  }, []);

  const handleLike = async (e: React.MouseEvent, charId: string) => {
    e.stopPropagation(); // Prevent opening character detail
    try {
      const response = await fetch(`${API_URL}/characters/${charId}/like`, {
        method: 'POST',
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        // Update liked IDs locally
        setLikedIds(prev => {
          const newSet = new Set(prev);
          if (data.liked) {
            newSet.add(charId);
          } else {
            newSet.delete(charId);
          }
          return newSet;
        });
        // Update character stats
        setCharacters(prev => prev.map(c => {
          if (c._id === charId) {
            return {
              ...c,
              stats: {
                ...c.stats,
                likes: data.likes,
                views: c.stats?.views || 0,
                messagesCount: c.stats?.messagesCount || 0,
                favorites: c.stats?.favorites || 0
              }
            };
          }
          return c;
        }));
      } else if (response.status === 401) {
        // Not authenticated - show login modal
        setShowLoginModal(true);
      }
    } catch (error) {
      console.error('Error liking character:', error);
    }
  };

  // Fetch user's liked character IDs (once on mount)
  const fetchLikedIds = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/user/liked-ids`, {
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success && data.likedIds) {
        setLikedIds(new Set(data.likedIds));
      }
    } catch (error) {
      console.error('Error fetching liked IDs:', error);
    }
  }, []);

  const { isDarkMode } = useTheme();

  const fetchData = async (pageNum: number = 1, reset: boolean = false) => {
    try {
      let charactersData;

      // If Favorites filter is active, fetch from favorites endpoint
      if (activeFilter === 'Favorites') {
        const favoritesResponse = await fetch(`${API_URL}/characters/user/favorites`, {
          credentials: 'include'
        });
        const favoritesData = await favoritesResponse.json();

        if (favoritesData.success) {
          // Apply NSFW filter on client side for favorites
          let filteredFavorites = favoritesData.characters;
          if (!showNSFW) {
            filteredFavorites = filteredFavorites.filter((c: Character) => !c.isNSFW);
          }
          // Apply search filter on client side for favorites
          if (searchText) {
            const searchLower = searchText.toLowerCase();
            filteredFavorites = filteredFavorites.filter((c: Character) =>
              c.name.toLowerCase().includes(searchLower) ||
              c.description.toLowerCase().includes(searchLower)
            );
          }
          setCharacters(filteredFavorites);
          setHasMore(false); // No pagination for favorites
        } else {
          setCharacters([]);
        }
        setLoading(false);
        return;
      }

      // Determine endpoint and params based on filter
      let endpoint = `${API_URL}/characters/public`;
      const queryParams = new URLSearchParams();
      queryParams.append('page', pageNum.toString());
      queryParams.append('limit', ITEMS_PER_PAGE.toString());
      if (searchText) queryParams.append('search', searchText);

      if (activeFilter === 'My Characters') {
        endpoint = `${API_URL}/characters/user`;
      } else {
        // Public filters
        if (showNSFW) queryParams.append('nsfw', 'true');
        if (activeFilter === 'New') queryParams.append('sort', 'newest');
      }

      const charactersResponse = await fetch(`${endpoint}?${queryParams.toString()}`, {
        credentials: 'include'
      });
      charactersData = await charactersResponse.json();

      if (charactersData.success) {
        let chars = charactersData.characters;

        // Client-side sort based on active filter
        if (activeFilter === 'New') {
          chars = [...chars].sort((a: any, b: any) =>
            new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime()
          );
        } else if (activeFilter === 'Popular') {
          chars = [...chars].sort((a: any, b: any) =>
            (b.stats?.views || 0) - (a.stats?.views || 0)
          );
        }

        // Check if more pages available
        if (chars.length < ITEMS_PER_PAGE) {
          setHasMore(false);
        }

        // Reset or append
        if (reset || pageNum === 1) {
          setCharacters(chars);
        } else {
          setCharacters(prev => [...prev, ...chars]);
        }

        // Fetch chats to find recent characters (only on first page)
        if (pageNum === 1) {
          const chatsResponse = await fetch(`${API_URL}/chats`, {
            credentials: 'include'
          });
          const chatsData = await chatsResponse.json();

          if (chatsData.success && chatsData.chats) {
            const uniqueCharIds = new Set<string>();
            const recentChars: Character[] = [];

            chatsData.chats.forEach((chat: any) => {
              if (chat.characterId && !uniqueCharIds.has(chat.characterId._id)) {
                if (recentChars.length < 15) {
                  uniqueCharIds.add(chat.characterId._id);
                  // Usar el characterId poblado directamente desde el chat
                  recentChars.push(chat.characterId as Character);
                }
              }
            });
            setRecentCharacters(recentChars);
          }
        }
      }
    } catch (error) {
      console.error('Error loading lobby data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1); // Reset page on filter change
      setHasMore(true);
      fetchData(1, true); // Force refresh
    }, 500); // Debounce search

    return () => clearTimeout(timer);
  }, [searchText, showNSFW, activeFilter]);

  // Fetch user profile and liked IDs on mount
  useEffect(() => {
    fetchUserProfile();
    fetchLikedIds();
  }, [fetchUserProfile, fetchLikedIds]);

  // Infinite scroll handler
  const handleScroll = useCallback(() => {
    if (!scrollContainerRef.current || loadingMore || !hasMore) return;

    const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
    // Load more when 200px from bottom
    if (scrollHeight - scrollTop - clientHeight < 200) {
      loadMoreCharacters();
    }
  }, [loadingMore, hasMore]);

  // Attach scroll listener
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const loadMoreCharacters = async () => {
    if (loadingMore || !hasMore) return;

    setLoadingMore(true);
    const nextPage = page + 1;

    try {
      const queryParams = new URLSearchParams();
      queryParams.append('page', nextPage.toString());
      queryParams.append('limit', ITEMS_PER_PAGE.toString());
      if (searchText) queryParams.append('search', searchText);

      let endpoint = `${API_URL}/characters/public`;

      if (activeFilter === 'My Characters') {
        endpoint = `${API_URL}/characters/user`;
      } else {
        if (showNSFW) queryParams.append('nsfw', 'true');
        if (activeFilter === 'New') queryParams.append('sort', 'newest');
      }

      const response = await fetch(`${endpoint}?${queryParams.toString()}`, {
        credentials: 'include'
      });
      const data = await response.json();

      if (data.success && data.characters) {
        if (data.characters.length < ITEMS_PER_PAGE) {
          setHasMore(false);
        }
        setCharacters(prev => [...prev, ...data.characters]);
        setPage(nextPage);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error('Error loading more characters:', error);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleCharacterClick = (character: Character) => {
    onViewCharacterDetail(character);
  };

  const handleCharacterManagerClose = () => {
    setShowCharacterManager(false);
    fetchData();
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100dvh',
        backgroundColor: isDarkMode ? '#1a1a2e' : '#fafafa',
        color: isDarkMode ? 'white' : 'black'
      }}>
        Loading EcstasyAI...
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100dvh',
      backgroundColor: isDarkMode ? '#0f0f1e' : '#f8fafc',
      color: isDarkMode ? '#e2e8f0' : '#1e293b',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      transition: 'background-color 0.3s, color 0.3s'
    }}>
      <Sidebar
        onCreateCharacter={() => {
          setStartInCreateMode(true);
          setShowCharacterManager(true);
          setIsMobileSidebarOpen(false);
        }}
        onMyCharacters={() => {
          setStartInCreateMode(false);
          setShowCharacterManager(true);
          setIsMobileSidebarOpen(false);
        }}
        onProfileUpdate={fetchUserProfile}
        onCollapse={(collapsed) => setIsSidebarCollapsed(collapsed)}
        isMobileOpen={isMobileSidebarOpen}
        onMobileClose={() => setIsMobileSidebarOpen(false)}
        onLogin={onLogin}
        onLogout={onLogout}
        onOpenPricing={onOpenPricing}
        userData={{
          username: userStats?.username || 'Guest',
          avatar: userStats?.avatar || null,
          isPro: userStats?.subscription?.tier && userStats.subscription.tier !== 'Free',
          usage: userStats?.usage,
          gender: userStats?.gender,
          birthday: userStats?.birthday,
          bio: userStats?.bio,
          email: userStats?.email,
          isOAuthUser: userStats?.isOAuthUser,
          hasPassword: userStats?.hasPassword
        }}
      />
      <TopBar
        onSearch={setSearchText}
        marginLeft={isMobile ? '0' : (isSidebarCollapsed ? '70px' : '240px')}
        onOpenMobileMenu={() => setIsMobileSidebarOpen(true)}
        userData={userStats ? {
          username: userStats.username,
          avatar: userStats.avatar,
          subscription: userStats.subscription,
          usage: userStats.usage,
          canClaim: userStats.canClaim
        } : undefined}
        onRefreshUserData={fetchUserProfile}
        onOpenProfile={async () => {
          await fetchUserProfile();
          setIsProfileModalOpen(true);
        }}
      />

      <ProfileModal
        isOpen={isProfileModalOpen}
        onClose={() => setIsProfileModalOpen(false)}
        userData={userStats}
        onUpdateProfile={async (data) => {
          try {
            const response = await fetch(`${API_URL}/user`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(data),
              credentials: 'include'
            });
            if (response.ok) {
              await fetchUserProfile();
            }
          } catch (error) {
            console.error('Error updating profile:', error);
          }
        }}
      />

      <div
        ref={scrollContainerRef}
        style={{
          padding: isMobile
            ? (recentCharacters.length > 0 ? '0 16px 100px 16px' : '20px 16px 100px 16px')
            : (recentCharacters.length > 0 ? '0 40px 30px 40px' : '20px 40px 30px 40px'),
          marginLeft: isMobile ? '0' : (isSidebarCollapsed ? '70px' : '240px'),
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          height: 'calc(100dvh - 80px)',
          overflowY: 'auto'
        }}>


        {/* Recent Chats Section */}
        {recentCharacters.length > 0 && (
          <div style={{
            marginBottom: '32px'
          }}>
            <h3 style={{
              fontSize: '18px',
              fontWeight: '600',
              marginBottom: '16px',
              color: isDarkMode ? '#e2e8f0' : '#334155'
            }}>Recent Chats</h3>

            <div style={{
              display: 'flex',
              gap: '16px',
              overflowX: 'auto',
              paddingBottom: '12px',
              // Escape parent padding on mobile to reach screen edges
              paddingLeft: isMobile ? '16px' : '8px',
              paddingRight: isMobile ? '16px' : '8px',
              marginLeft: isMobile ? '-16px' : '-8px',
              marginRight: isMobile ? '-16px' : '-8px',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none'
            }}>
              {recentCharacters.map(char => (
                <div
                  key={char._id}
                  onClick={() => onSelectCharacter(char)}
                  style={{
                    flex: '0 0 auto',
                    width: '100px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    cursor: 'pointer',
                    transition: 'transform 0.2s',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-5px)'}
                  onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                >
                  <div style={{
                    width: '80px',
                    height: '80px',
                    borderRadius: '50%',
                    overflow: 'hidden',
                    marginBottom: '8px',
                    border: '2px solid #a855f7',
                    boxShadow: '0 4px 10px rgba(168, 85, 247, 0.3)'
                  }}>
                    <img
                      src={char.avatar || '/placeholder-user.jpg'}
                      alt={char.name}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                  </div>
                  <span style={{
                    fontSize: '13px',
                    fontWeight: '500',
                    textAlign: 'center',
                    color: isDarkMode ? '#cbd5e1' : '#475569',
                    maxWidth: '100%',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}>
                    {char.name}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Filter Bar */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px',
          flexWrap: 'wrap',
          gap: '12px'
        }}>
          <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '4px' }} role="tablist">
            {['Popular', 'New', 'Favorites', 'My Characters'].map(filter => (
              <button
                key={filter}
                onClick={() => setActiveFilter(filter)}
                role="tab"
                aria-selected={activeFilter === filter}
                aria-label={`Filter by ${filter}`}
                style={{
                  padding: '8px 16px',
                  borderRadius: '20px',
                  border: activeFilter === filter ? 'none' : `1px solid ${isDarkMode ? 'rgba(255,255,255,0.2)' : '#e2e8f0'}`,
                  backgroundColor: activeFilter === filter ? '#a855f7' : (isDarkMode ? '#1a1a2e' : '#ffffff'),
                  color: activeFilter === filter ? 'white' : (isDarkMode ? '#e2e8f0' : '#1e293b'),
                  fontWeight: '600',
                  fontSize: '13px',
                  cursor: 'pointer',
                  boxShadow: activeFilter === filter ? '0 4px 12px rgba(168, 85, 247, 0.3)' : '0 1px 2px rgba(0,0,0,0.05)',
                  whiteSpace: 'nowrap',
                  transition: 'background-color 0.3s, color 0.3s'
                }}
              >
                {activeFilter === filter && <span style={{ marginRight: 4 }}>🔥</span>}
                {filter}
              </button>
            ))}
          </div>

          <div
            onClick={() => {
              const newValue = !showNSFW;
              setShowNSFW(newValue);
              localStorage.setItem('showNSFW', String(newValue));
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 16px',
              backgroundColor: showNSFW ? 'rgba(239, 68, 68, 0.1)' : (isDarkMode ? '#1a1a2e' : '#ffffff'),
              border: `1px solid ${showNSFW ? '#ef4444' : (isDarkMode ? 'rgba(255,255,255,0.1)' : '#e2e8f0')}`,
              borderRadius: '20px',
              cursor: 'pointer',
              transition: 'all 0.3s'
            }}
          >
            <div style={{
              width: '40px',
              height: '24px',
              backgroundColor: showNSFW ? '#ef4444' : '#94a3b8',
              borderRadius: '12px',
              position: 'relative',
              transition: 'background-color 0.3s'
            }}>
              <div style={{
                width: '20px',
                height: '20px',
                backgroundColor: 'white',
                borderRadius: '50%',
                position: 'absolute',
                top: '2px',
                left: showNSFW ? '18px' : '2px',
                transition: 'left 0.3s',
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
              }} />
            </div>
            <span style={{
              fontWeight: '600',
              fontSize: '13px',
              color: showNSFW ? '#ef4444' : (isDarkMode ? '#94a3b8' : '#64748b')
            }}>
              NSFW 18+
            </span>
          </div>
        </div>


        {/* Cards Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fill, minmax(214px, 1fr))',
          gap: isMobile ? '12px' : '24px'
        }}>
          {characters
            .filter(character => {
              if (!searchText.trim()) return true;
              const search = searchText.toLowerCase();
              return (
                character.name.toLowerCase().includes(search) ||
                character.description.toLowerCase().includes(search) ||
                character.tags.some(tag => tag.toLowerCase().includes(search))
              );
            })
            .map((character) => (
              <div
                key={character._id}
                onClick={() => handleCharacterClick(character)}
                style={{
                  height: isMobile ? '290px' : '360px',
                  borderRadius: isMobile ? '16px' : '20px',
                  position: 'relative',
                  overflow: 'hidden',
                  cursor: 'pointer',
                  boxShadow: isDarkMode ? '0 10px 20px rgba(0,0,0,0.5)' : '0 10px 20px rgba(0,0,0,0.1)',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                  backgroundColor: isDarkMode ? '#1e1e2e' : 'white',
                  border: isDarkMode ? '1px solid rgba(255,255,255,0.05)' : 'none'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-6px)';
                  e.currentTarget.style.boxShadow = isDarkMode ? '0 20px 30px rgba(0,0,0,0.6)' : '0 20px 30px rgba(0,0,0,0.15)';
                  // Zoom image slightly
                  const img = e.currentTarget.querySelector('img');
                  if (img) img.style.transform = 'scale(1.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = isDarkMode ? '0 10px 20px rgba(0,0,0,0.5)' : '0 10px 20px rgba(0,0,0,0.1)';
                  const img = e.currentTarget.querySelector('img');
                  if (img) img.style.transform = 'scale(1)';
                }}
              >
                {/* Full Height Image */}
                <div style={{
                  height: '100%',
                  width: '100%',
                  position: 'absolute',
                  top: 0,
                  left: 0
                }}>
                  <img
                    src={character.avatar || character.backgroundImage}
                    alt={character.name}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      transition: 'transform 0.5s ease',
                      transformOrigin: 'center center' // Ensure zoom is centered
                    }}
                  />
                </div>

                {/* Transparent Overlay Content Section */}
                <div style={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  padding: '16px',
                  paddingTop: '40px', // Extra space for gradient fade
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-end',
                  background: isDarkMode
                    ? 'linear-gradient(to top, rgba(15,15,20,0.95) 0%, rgba(15,15,20,0.7) 60%, transparent 100%)'
                    : 'linear-gradient(to top, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.8) 60%, transparent 100%)',
                  // backdropFilter: 'blur(2px)', // Optional: subtle blur for readability
                }}>

                  {/* Title and Description */}
                  <div style={{ marginBottom: '10px' }}>
                    <h3 style={{
                      fontSize: isMobile ? '16px' : '18px',
                      fontWeight: '800',
                      marginBottom: '2px',
                      color: isDarkMode ? 'white' : '#1e293b',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      textShadow: isDarkMode ? '0 2px 4px rgba(0,0,0,0.5)' : 'none'
                    }} title={character.name}>
                      {character.name}
                    </h3>

                    {character.role && (
                      <p style={{
                        fontSize: '11px',
                        color: isDarkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)',
                        margin: '0 0 4px 0',
                        fontWeight: '600',
                        letterSpacing: '0.3px',
                        textShadow: isDarkMode ? '0 1px 2px rgba(0,0,0,0.5)' : 'none'
                      }}>
                        {character.role}
                      </p>
                    )}

                    <p style={{
                      fontSize: '12px',
                      color: isDarkMode ? 'rgba(255,255,255,0.8)' : '#475569',
                      marginBottom: '0',
                      lineHeight: '1.4',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      height: '34px',
                      textShadow: isDarkMode ? '0 1px 2px rgba(0,0,0,0.5)' : 'none'
                    }}>
                      {character.description}
                    </p>
                  </div>

                  {/* Tags and Stats Container */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>

                    {/* Tags */}
                    <div style={{
                      display: 'flex',
                      gap: '6px',
                      overflowX: 'hidden',
                      whiteSpace: 'nowrap',
                      maskImage: 'linear-gradient(to right, black 85%, transparent 100%)',
                      WebkitMaskImage: 'linear-gradient(to right, black 85%, transparent 100%)'
                    }}>
                      {character.tags.slice(0, 3).map((tag, i) => (
                        <span key={i} style={{
                          fontSize: '10px',
                          padding: '3px 8px',
                          backgroundColor: i === 0
                            ? 'rgba(236, 72, 153, 0.25)'
                            : 'rgba(255, 255, 255, 0.1)',
                          borderRadius: '6px',
                          color: i === 0
                            ? '#f9a8d4'
                            : (isDarkMode ? '#e2e8f0' : '#475569'),
                          fontWeight: '700',
                          border: '1px solid rgba(255,255,255,0.1)',
                          backdropFilter: 'blur(4px)',
                          flexShrink: 0
                        }}>
                          {tag.toUpperCase()}
                        </span>
                      ))}
                    </div>

                    {/* Stats */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      color: isDarkMode ? 'rgba(255,255,255,0.6)' : '#64748b',
                      fontSize: '11px',
                      fontWeight: '600'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '3px', marginRight: '12px' }}>
                        <Eye size={13} style={{ opacity: 0.8 }} /> {character.stats?.views || 0}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                        <MessageSquare size={13} style={{ opacity: 0.8 }} /> {character.stats?.messagesCount || 0}
                      </div>

                      <div
                        onClick={(e) => handleLike(e, character._id)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '3px',
                          marginLeft: 'auto',
                          cursor: 'pointer',
                          color: likedIds.has(character._id) ? '#ff6b6b' : 'inherit',
                          transition: 'transform 0.2s',
                          padding: '4px'
                        }}
                        onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.2)'}
                        onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
                      >
                        <Heart size={13} fill={likedIds.has(character._id) ? "#ff6b6b" : "none"} /> {character.stats?.likes || 0}
                      </div>
                    </div>
                  </div>

                </div>
              </div>
            ))}

          {/* Loading more indicator */}
          {loadingMore && (
            <div style={{
              gridColumn: '1 / -1',
              display: 'flex',
              justifyContent: 'center',
              padding: '20px'
            }}>
              <Loader2 size={24} style={{ color: isDarkMode ? '#a78bfa' : '#7c3aed', animation: 'spin 1s linear infinite' }} />
            </div>
          )}

          {!hasMore && characters.length > 0 && (
            <div style={{
              gridColumn: '1 / -1',
              textAlign: 'center',
              padding: '20px',
              color: 'rgba(255,255,255,0.5)',
              fontSize: '14px'
            }}>
              No more characters to load
            </div>
          )}
        </div>
      </div>

      {
        showCharacterManager && (
          <CharacterManager
            isOpen={showCharacterManager}
            onClose={handleCharacterManagerClose}
            startInCreateMode={startInCreateMode}
            marginLeft={isMobile ? '0' : (isSidebarCollapsed ? '70px' : '240px')}
          />
        )
      }

      {/* Mobile Bottom Navigation - Hide when CharacterManager is open */}
      {
        isMobile && !showCharacterManager && (
          <MobileNavigation
            activeTab="home"
            onHome={() => { }}
            onCreateCharacter={() => {
              setStartInCreateMode(true);
              setShowCharacterManager(true);
            }}
            onShowChats={() => { }}
            onProfile={() => setIsProfileModalOpen(true)}
          />
        )
      }

      {/* Login Modal for non-authenticated like attempts */}
      <LoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onLoginSuccess={() => {
          setShowLoginModal(false);
          fetchLikedIds(); // Refresh liked IDs after login
        }}
      />
    </div >
  );
};

export default CharacterLobby;

